import React from "react";
import { graphql } from "gatsby";

import Layout from "components/Layout";
import SEO, { createQueryImageFromFile } from "components/SEO";

import { NewsroomTemplate } from "./innerTemplates/NewsroomTemplate";

const DATE_PUBLISHED = new Date("2019-08-12T12:51:31+00:00");

export const pageQuery = graphql`
  query NewsroomPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        description
        sharingImage {
          image {
            ...OpenGraphImage
            ...TwitterImage
            ...StructuredDataImage
          }
          alt
          caption
        }
        featuredPostLink {
          linkText
          linkIcon
        }
        topPicksLabel
        featuredPost {
          ...FeaturedPost
        }
        topPicksPosts {
          ...BlogPostThumbnail
        }
      }
    }
    # Search index for the page
    localSearchNews {
      index
    }
    # Get all posts sorted by date
    allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: { frontmatter: { template: { eq: "news" } } }
    ) {
      tags: distinct(field: frontmatter___tags)
      edges {
        node {
          ...BlogPostThumbnail
        }
      }
    }
  }
`;

/**
 * @typedef {object} BlogProps
 * @prop {Location} location
 * @prop {GatsbyTypes.NewsroomPageQuery} data
 * @param {BlogProps} props
 */
export default function Newsroom(props) {
  const {
    location: { pathname },
    data: {
      markdownRemark: {
        frontmatter: {
          title: pageTitle,
          description,
          sharingImage,
          featuredPostLink,
          topPicksLabel,
          featuredPost,
          topPicksPosts,
        },
      },
      allMarkdownRemark,
      localSearchNews,
    },
  } = props;

  const [openGraph, twitter, sdPrimaryImage] = createQueryImageFromFile(
    sharingImage,
    ["openGraphImage.resize", "twitterImage.resize", "sdImage.resize"]
  );

  const latestUpdate =
    allMarkdownRemark.edges.length > 0
      ? allMarkdownRemark.edges.reduce((acc, post) => {
          return new Date(
            Math.max(
              new Date(post.node.fields.dateModified).getTime(),
              new Date(acc).getTime()
            )
          );
        }, new Date(allMarkdownRemark.edges[0].node.fields.dateModified))
      : new Date();

  return (
    <Layout gradientBackground>
      <SEO
        pathname={pathname}
        title={pageTitle}
        description={description}
        images={{ openGraph, twitter, sdPrimaryImage }}
        datePublished={DATE_PUBLISHED}
        dateModified={latestUpdate}
      />
      <NewsroomTemplate
        featuredPost={featuredPost}
        topPicksPosts={topPicksPosts}
        tags={allMarkdownRemark.tags}
        blogPosts={allMarkdownRemark.edges.map(({ node }) => node)}
        searchIndex={localSearchNews.index}
        featuredPostLinkButtonIcon={
          (featuredPostLink && featuredPostLink.linkIcon) || "arrowForward"
        }
        featuredPostLinkButtonText={
          (featuredPostLink && featuredPostLink.linkText) || "Read more"
        }
        topPicksPostsLabelText={topPicksLabel || "Top Picks"}
      />
    </Layout>
  );
}
